import { i18next } from "@/i18n";
import { AddonOfChargeApiInterface } from "@/ts/interfaces/Checkout/api/AddonOfChargeApiInterface";
import { UserInterface } from "@/ts/interfaces/Parent/UserInterface";

import { PLAN_TYPE } from "@ts/enums/PlanTypesEnum.ts";
import { AddonInterface } from "@ts/interfaces/Checkout/AddonInterface";
import { GradeInterface } from "@ts/interfaces/Checkout/GradeInterface";
import { PlanInterface } from "@ts/interfaces/Checkout/PlanInterface";
import { ProductFamilyInterface } from "@ts/interfaces/Checkout/ProductFamilyInterface";
import { UserCheckoutStateInterface } from "@ts/interfaces/Checkout/UserCheckoutStateInterface";
import { AddonApiInterface } from "@ts/interfaces/Checkout/api/AddonApiInterface";
import { GradeApiInterface } from "@ts/interfaces/Checkout/api/GradeApiInterface";
import { PlanApiInterface } from "@ts/interfaces/Checkout/api/PlanApiInterface.ts";
import { ProductFamilyApiInterface } from "@ts/interfaces/Checkout/api/ProductFamilyApiInterface.ts";
import { UnifiedPremiumServiceApiInterface } from "@ts/interfaces/Checkout/api/UnifiedPremiumServiceApiInterface";

import { resolveLearningModeAddon } from "@utils/learningMode";

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Converts price from cents to dollars
 */
export const convertCentsToDollars = (cents: number): number => cents / 100;

/**
 * Formats price with currency
 */
export const formatPrice = (price: number, currency: string): string =>
    `$${price} ${currency}`;

/**
 * Constructs full asset URL
 */
export const getAssetUrl = (assetPath: string): string =>
    `${process.env.ASSET_URL}/build/assets/${assetPath}`;

/**
 * Determines plan type based on period and number of instalments
 */
export const determinePlanType = (
    period: string,
    numberOfInstalments: number
): PLAN_TYPE => {
    if (period === "year" && numberOfInstalments === 1) {
        return PLAN_TYPE.YEARLY;
    }
    if (period === "year" && numberOfInstalments > 1) {
        return PLAN_TYPE.INSTALLMENT;
    }
    if (period === "month" && numberOfInstalments > 1) {
        return PLAN_TYPE.QUARTERLY;
    }
    return PLAN_TYPE.MONTHLY;
};

export interface FeatureMetadata {
    name: string;
    isActive: boolean;
}

export interface Feature {
    id: number;
    text: string;
    isAvailable: boolean;
}

export interface AddonMetadata {
    features: Feature[];
    tagline: string | null;
    includesText: string | null;
}

const GRADE_PLACEHOLDER_REGEX = /\{grade\}/g;

/**
 * Replace every `{grade}` placeholder in a metadata string with the selected
 * grade name (e.g. "Year 3").
 *
 * When no grade name is available the placeholder (plus one adjacent space)
 * is dropped so the copy still reads naturally:
 *   "Full {grade} Australian curriculum" -> "Full Australian curriculum"
 */
export const interpolateGrade = (text: string, gradeName?: string | null) => {
    if (!text.includes("{grade}")) {
        return text;
    }
    if (gradeName) {
        return text.replace(GRADE_PLACEHOLDER_REGEX, gradeName);
    }
    return text
        .replace(/ ?\{grade\} ?/g, " ")
        .replace(/\s{2,}/g, " ")
        .trim();
};

const toOptionalString = (
    value: unknown,
    transform: (text: string) => string
): string | null =>
    typeof value === "string" && value.trim() !== "" ? transform(value) : null;

/**
 * Shared metadata parser. `transform` is applied to every string (feature
 * names, tagline, includes_text) — used for `{grade}` interpolation.
 */
const parseMetadataWith = (
    metadata: string | null | undefined,
    transform: (text: string) => string
): AddonMetadata => {
    const empty: AddonMetadata = {
        features: [],
        tagline: null,
        includesText: null
    };

    if (!metadata) {
        return empty;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(metadata);
    } catch {
        return empty;
    }

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        return empty;
    }

    const {
        features,
        tagline,
        includes_text: includesTextRaw
    } = parsed as Record<string, unknown>;

    const parsedFeatures: Feature[] = Array.isArray(features)
        ? features
              .filter(
                  (f): f is FeatureMetadata =>
                      !!f &&
                      typeof f === "object" &&
                      typeof (f as FeatureMetadata).name === "string" &&
                      (f as FeatureMetadata).name.trim() !== ""
              )
              .map((f, index) => ({
                  id: index + 1,
                  text: transform(f.name),
                  isAvailable: f.isActive === true
              }))
        : [];

    return {
        features: parsedFeatures,
        tagline: toOptionalString(tagline, transform),
        includesText: toOptionalString(includesTextRaw, transform)
    };
};

const identity = (text: string) => text;

/**
 * Parse an add-on / charge / plan `metadata` JSON string.
 *
 * Contract (Chargebee metadata exposed via the product-families JSON):
 *   {"features":[{"name":string,"isActive":bool}], "tagline"?: string, "includes_text"?: string}
 *
 * Robust against null/empty strings, "[]", "{}", invalid JSON, non-array
 * `features` and feature entries without a string `name`. `{grade}`
 * placeholders are interpolated with `gradeName` (see interpolateGrade).
 * Plain text only.
 */
export const parseAddonMetadata = (
    metadata: string | null | undefined,
    gradeName?: string | null
): AddonMetadata =>
    parseMetadataWith(metadata, text => interpolateGrade(text, gradeName));

/**
 * Constructs features array from metadata JSON string.
 *
 * `{grade}` placeholders are preserved — interpolate at render time with the
 * selected grade (see interpolateGrade / AddonFeatureList).
 */
export const constructFeatures = (metadata: string): Feature[] =>
    parseMetadataWith(metadata, identity).features;

/**
 * Raw (non-interpolated) tagline / includes_text for an add-on's metadata.
 * `{grade}` placeholders are preserved for render-time interpolation.
 */
const constructAddonCopy = (
    metadata: string | null | undefined
): Pick<AddonInterface, "tagline" | "includesText"> => {
    const { tagline, includesText } = parseMetadataWith(metadata, identity);
    return { tagline, includesText };
};

export interface PlanPriceInfo {
    period: string;
    billingCycle: number;
    numberOfInstalments: number;
    nextPaymentInMonths: number;
    currency: string;
    price: number;
}

export interface PlanPriceLabels {
    titlePeriodStr: string;
    priceTitle: string;
    planDescription: string;
    description: string;
    planType: PLAN_TYPE;
    priceSubTitle: string;
    numberOfInstalments: number;
    nextPaymentInMonths: number;
}

/**
 * Generates price labels and descriptions for a plan
 */
export const generatePlanPriceLabels = (
    priceInfo: PlanPriceInfo
): PlanPriceLabels => {
    const {
        period,
        billingCycle,
        numberOfInstalments,
        nextPaymentInMonths,
        currency,
        price
    } = priceInfo;

    const priceInDollars = convertCentsToDollars(price);
    const planType = determinePlanType(period, numberOfInstalments);

    let titlePeriodStr = "";
    let planDescription = "";
    let description = "";
    let priceSubTitle = "";

    if (planType === PLAN_TYPE.YEARLY) {
        titlePeriodStr = billingCycle === 0 ? i18next.t("per year") : "";
        planDescription = i18next.t("Yearly Plan");
        description = i18next.t("Paid Upfront");
    } else if (planType === PLAN_TYPE.INSTALLMENT) {
        titlePeriodStr =
            billingCycle === 0
                ? i18next.t("every {{ nextPaymentInMonths }} months", {
                      nextPaymentInMonths
                  })
                : "";
        priceSubTitle = i18next.t(
            "Total yearly commitment ${{ price }} {{ currency }}",
            { price: priceInDollars * numberOfInstalments, currency }
        );
        planDescription = i18next.t(
            "Yearly Plan ({{ installments }} x Instalments)",
            { installments: numberOfInstalments }
        );
        description = i18next.t("Paid over {{ installments }} instalments", {
            installments: numberOfInstalments
        });
    } else if (planType === PLAN_TYPE.QUARTERLY) {
        titlePeriodStr =
            billingCycle === 0
                ? i18next.t("every {{ nextPaymentInMonths }} months", {
                      nextPaymentInMonths
                  })
                : "";
        planDescription = i18next.t("Term Plan");
        description = "";
    } else if (planType === PLAN_TYPE.MONTHLY) {
        titlePeriodStr = billingCycle === 0 ? i18next.t("every month") : "";
        planDescription = i18next.t("Monthly Plan");
        description = "";
    }

    return {
        titlePeriodStr,
        priceTitle: formatPrice(priceInDollars, currency),
        planDescription,
        description,
        planType,
        priceSubTitle,
        numberOfInstalments,
        nextPaymentInMonths
    };
};

export interface AddonPriceLabels {
    priceSubTitle: string;
    priceFooterText1: string;
    priceFooterText2: string;
}

/**
 * Generates price labels for an addon based on plan context
 */
export const generateAddonPriceLabels = (
    planType: PLAN_TYPE,
    nextPaymentInMonths: number,
    numberOfInstalments: number,
    addonPrice: number,
    currency: string
): AddonPriceLabels => {
    const priceSubTitle =
        planType === PLAN_TYPE.INSTALLMENT
            ? i18next.t("every {{ nextPaymentInMonths }} months", {
                  nextPaymentInMonths
              })
            : "";

    const priceFooterText1 =
        planType === PLAN_TYPE.INSTALLMENT
            ? i18next.t("Paid in {{ numberOfInstalments }} instalments", {
                  numberOfInstalments
              })
            : "";

    const priceFooterText2 =
        planType === PLAN_TYPE.INSTALLMENT
            ? i18next.t("Total amount ${{ price }} {{ currency }}", {
                  price: addonPrice * numberOfInstalments,
                  currency
              })
            : "";

    return {
        priceSubTitle,
        priceFooterText1,
        priceFooterText2
    };
};

// ============================================================================
// Main Mapper Functions
// ============================================================================

export const mapProductFamiliesApiData = (
    productFamilies: ProductFamilyApiInterface[]
): ProductFamilyInterface[] => {
    return productFamilies.map(p => ({
        id: p.id,
        coverImage: getAssetUrl(p.cover_image),
        description: p.grade_range_label,
        name: p.name,
        hasCharges: p.plans_contains_charges,
        unifiedPremiumServices: p.unified_premium_services ?? false,
        plans: p.plans
            ? mapPlansApiData(p.plans, p.unified_premium_services ?? false)
            : [],
        gradeRangeLabel: p.grade_range_label,
        selectMembershipDescription: p.select_membership_description,
        grades: p.grades ? mapGradesApiData(p.grades) : []
    }));
};

/**
 * Map plan API data to frontend PlanInterface.
 *
 * TTB-305: When `isUnified` is true, plan.addons contains the unified format
 * (UnifiedPremiumServiceApiInterface) and plan.charges is ignored.
 * When false (default), uses legacy dual-path: plan.addons (AddonApiInterface)
 * + plan.charges.addon (AddonOfChargeApiInterface).
 */
export const mapPlansApiData = (
    plans: PlanApiInterface[],
    isUnified: boolean = false
): PlanInterface[] => {
    return plans.map(p => {
        const priceLabels = generatePlanPriceLabels({
            period: p.price!.period,
            billingCycle: p.price!.billing_cycle,
            numberOfInstalments: p.price!.number_of_instalments,
            nextPaymentInMonths: p.price!.next_payment_in_months,
            currency: p.price!.currency,
            price: p.price!.price
        });

        const {
            description,
            planDescription,
            numberOfInstalments,
            planType,
            priceTitle,
            titlePeriodStr,
            priceSubTitle,
            nextPaymentInMonths
        } = priceLabels;

        /*
         * TTB-305: When unified_premium_services is true, all premium services
         * come through plan.addons in a single consistent format. The charges
         * path is not used — set to null.
         *
         * When false (legacy), use the old dual-path: plan.addons for recurring
         * addons + plan.charges.addon for one-time charge addons.
         */
        const addons = p.addons
            ? isUnified
                ? mapUnifiedServicesApiData(
                      p.addons as UnifiedPremiumServiceApiInterface[],
                      { planType, nextPaymentInMonths, numberOfInstalments }
                  )
                : mapAddonsApiData(p.addons as AddonApiInterface[], {
                      planType,
                      nextPaymentInMonths,
                      numberOfInstalments
                  })
            : null;

        const charges = isUnified
            ? null // Unified mode: no separate charges path
            : p.charges
              ? {
                    addons: p.charges.addon
                        ? mapAddonsOfChargeApiData(p.charges.addon)
                        : null
                }
              : null;

        return {
            id: p.id,
            name: p.name,
            priceId: p.price!.price_id,
            currency: p.price!.currency,
            description,
            planDescription,
            type: planType,
            numberOfInstalments,
            features: constructFeatures(p.metadata),
            isHighlighted: p.is_highlighted,
            price: convertCentsToDollars(p.price!.price),
            highlightedText: p.highlighted_text,
            period: p.price!.period,
            reducedPrice: 0,
            title: priceTitle,
            priceSubTitle,
            addons,
            targetGradeId: p.target_grade ? p.target_grade.id : null,
            targetGradeName: p.target_grade ? p.target_grade.name : undefined,
            titlePeriod: titlePeriodStr,
            order: p.order,
            crossedPriceLabel:
                p.price!.period === "year" &&
                p.price!.next_payment_in_months === 12 &&
                p.price!.yearly_price
                    ? formatPrice(p.price!.yearly_price, p.price!.currency)
                    : undefined,
            isPreSelectedPlan: p.is_selected,
            charges
        };
    });
};

export const mapGradesApiData = (
    grades: GradeApiInterface[]
): GradeInterface[] => {
    return grades.map(g => ({
        id: g.id,
        name: g.name,
        shortName: g.short_name,
        year: g.year,
        upgradeAllowed: g.upgrade_allowed
    }));
};

export const mapAddonsOfChargeApiData = (
    addons: AddonOfChargeApiInterface[]
): AddonInterface[] => {
    return addons.map(a => {
        const priceInDollars = convertCentsToDollars(a.price);
        const discountPriceInDollars = convertCentsToDollars(a.discount_price);

        return {
            id: a.price_id,
            name: a.name,
            priceId: a.price_id,
            description: a.description,
            coverImage: getAssetUrl(a.cover_image),
            isHighlighted: a.is_highlighted,
            highlightedText: a.highlighted_text,
            price: priceInDollars,
            isIncluded: a.price <= 0,
            currency: a.currency,
            priceLabel: formatPrice(priceInDollars, a.currency),
            billingFrequency: "per year",
            crossedPriceLabel: formatPrice(discountPriceInDollars, a.currency),
            isBulkAddon: a.is_bulk_addon,
            entitlementsIds: a.entitlements_ids ?? [],
            entitlementsNames: a.entitlements_names ?? [],
            features: constructFeatures(a.metadata),
            ...constructAddonCopy(a.metadata),
            order: a.order
        };
    });
};

export const mapAddonsApiData = (
    addons: AddonApiInterface[],
    planInfo: {
        planType: PLAN_TYPE;
        nextPaymentInMonths: number;
        numberOfInstalments: number;
    }
): AddonInterface[] => {
    const { planType, nextPaymentInMonths, numberOfInstalments } = planInfo;

    return addons.map(a => {
        const priceInDollars = convertCentsToDollars(a.price.price);
        const discountPriceInDollars = convertCentsToDollars(
            a.price.discount_price
        );

        const { priceFooterText1, priceFooterText2, priceSubTitle } =
            generateAddonPriceLabels(
                planType,
                nextPaymentInMonths,
                numberOfInstalments,
                priceInDollars,
                a.price.currency
            );

        return {
            id: a.id,
            name: a.name,
            priceId: a.price.price_id,
            description: a.description,
            coverImage: getAssetUrl(a.cover_image),
            isHighlighted: a.is_highlighted,
            highlightedText: a.highlighted_text,
            price: priceInDollars,
            isIncluded: a.price.price <= 0,
            currency: a.price.currency,
            priceLabel: formatPrice(priceInDollars, a.price.currency),
            billingFrequency:
                a.price.period === "year" ? "per year" : "per term",
            crossedPriceLabel: formatPrice(
                discountPriceInDollars,
                a.price.currency
            ),
            isBulkAddon: a.is_bulk_addon,
            addonCategory: a.addon_category,
            features: constructFeatures(a.metadata),
            ...constructAddonCopy(a.metadata),
            order: a.order,
            priceFooterText1,
            priceFooterText2,
            priceSubTitle
        };
    });
};

/**
 * TTB-305: Map unified premium service API data to AddonInterface.
 *
 * Used when unified_premium_services flag is true. All services (recurring
 * and one-time) come in the same format with a nested price object.
 * This replaces the need for two separate mappers (mapAddonsApiData +
 * mapAddonsOfChargeApiData).
 */
export const mapUnifiedServicesApiData = (
    services: UnifiedPremiumServiceApiInterface[],
    planInfo: {
        planType: PLAN_TYPE;
        nextPaymentInMonths: number;
        numberOfInstalments: number;
    }
): AddonInterface[] => {
    const { planType, nextPaymentInMonths, numberOfInstalments } = planInfo;

    return services.map(s => {
        const priceInDollars = convertCentsToDollars(s.price.price);
        const discountPriceInDollars = convertCentsToDollars(
            s.price.discount_price
        );

        const { priceFooterText1, priceFooterText2, priceSubTitle } =
            generateAddonPriceLabels(
                planType,
                nextPaymentInMonths,
                numberOfInstalments,
                priceInDollars,
                s.price.currency
            );

        return {
            id: s.id,
            name: s.name,
            priceId: s.price.price_id,
            description: s.description,
            coverImage: getAssetUrl(s.cover_image),
            isHighlighted: s.is_highlighted,
            highlightedText: s.highlighted_text,
            price: priceInDollars,
            isIncluded: s.price.price <= 0,
            currency: s.price.currency,
            priceLabel: formatPrice(priceInDollars, s.price.currency),
            billingFrequency:
                s.billing_type === "one_time"
                    ? "one-off"
                    : s.price.period === "year"
                      ? "per year"
                      : "per term",
            crossedPriceLabel: formatPrice(
                discountPriceInDollars,
                s.price.currency
            ),
            isBulkAddon: s.is_bundle,
            billingType: s.billing_type,
            addonCategory: s.addon_category,
            entitlementsIds: s.entitlements_ids ?? [],
            entitlementsNames: s.entitlements_names ?? [],
            features: constructFeatures(s.metadata),
            ...constructAddonCopy(s.metadata),
            order: s.order,
            priceFooterText1,
            priceFooterText2,
            priceSubTitle
        };
    });
};

export const mapTokenDataToCheckoutAtom = (
    checkoutState: UserCheckoutStateInterface,
    productFamilies: ProductFamilyInterface[],
    students: UserInterface[]
) => {
    const {
        STUDENT_SELECTION_PAGE,
        CREATE_NEW_STUDENT_PAGE,
        PRODUCT_FAMILY_SELECT_PAGE,
        PLAN_SELECT_PAGE,
        ADDON_SELECT_PAGE,
        LEARNING_MODE_PAGE,
        USER_INFO_PAGE,
        nextAttemptPage
    } = checkoutState;

    const selectedStudent =
        students?.find(student => student.id === STUDENT_SELECTION_PAGE?.id) ??
        null;

    const student = CREATE_NEW_STUDENT_PAGE
        ? {
              firstName: CREATE_NEW_STUDENT_PAGE.firstName ?? null,
              lastName: CREATE_NEW_STUDENT_PAGE.lastName ?? null
          }
        : null;

    const productFamily =
        productFamilies.find(pf => pf.id === PRODUCT_FAMILY_SELECT_PAGE?.id) ??
        null;

    const grade =
        productFamily?.grades.find(
            g => g.name === PRODUCT_FAMILY_SELECT_PAGE?.gradeName
        ) ?? null;

    const plan =
        productFamily?.plans.find(p => p.id === PLAN_SELECT_PAGE?.id) ?? null;

    const addon =
        plan?.addons?.find(a => a.id === ADDON_SELECT_PAGE?.id) ??
        plan?.charges?.addons?.find(a => a.id === ADDON_SELECT_PAGE?.id) ??
        null;

    const learningModeId = LEARNING_MODE_PAGE?.id ?? null;

    // Restore the learning mode selection from the token. Self-Directed is a
    // real $0 learning_mode add-on; the legacy `self-directed-program` id maps
    // to it. Billing filters $0 learning modes out of Chargebee price ids.
    const learningMode = resolveLearningModeAddon(learningModeId, plan?.addons);
    const learningModeChosen =
        checkoutState.learningModeChosen ?? LEARNING_MODE_PAGE !== undefined;

    return {
        user: {
            firstName: USER_INFO_PAGE.firstName,
            email: USER_INFO_PAGE.email,
            receiveOfferNews: USER_INFO_PAGE.receiveOfferNews
        },
        productFamily,
        plan,
        grade,
        addon,
        learningMode,
        learningModeChosen,
        nextAttemptPage,
        charge: null,
        selectedStudent,
        student
    };
};

export const mapQueryParamsDataToCheckoutState = (
    queryParams: {
        productFamilyId: string | null;
        gradeName: string | null;
        planId: string | null;
        addonId: string | null;
        learningModeId?: string | null;
    },
    productFamilies: ProductFamilyInterface[]
) => {
    const { productFamilyId, gradeName, planId, addonId, learningModeId } =
        queryParams;

    const productFamily =
        productFamilies.find(pf => pf.id === productFamilyId) ?? null;

    const grade = productFamily?.grades.find(g => g.name === gradeName) ?? null;

    const plan = productFamily?.plans.find(p => p.id === planId) ?? null;

    const addon = plan?.addons?.find(a => a.id === addonId) ?? null;

    // Resolve the learning mode selection from the URL param id. The legacy
    // `self-directed-program` id maps to the plan's $0 learning_mode add-on.
    const learningMode = resolveLearningModeAddon(learningModeId, plan?.addons);
    const learningModeChosen = learningModeId != null;

    return {
        productFamily,
        grade,
        plan,
        addon,
        learningMode,
        learningModeChosen
    };
};
